import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Phone } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { formatBudget, formatDateTime, formatRelative } from '@/lib/format';
import { ACTIVE_STAGES, type LeadStage } from '@/types/app';
import { MatchingUnitsPanel } from '@/features/inventory/MatchingUnitsPanel';
import { DealsPanel } from '@/features/deals/DealsPanel';
import { WhatsAppDialog } from '@/features/whatsapp/WhatsAppDialog';
import { AiPanel } from '@/features/ai/AiPanel';
import { EditLeadDialog } from './EditLeadDialog';
import { LogActivityDialog } from './LogActivityDialog';
import { SlaCountdown } from './SlaCountdown';
import { SlaBadge, StageBadge, TemperatureBadge } from './StageBadge';
import { Timeline } from './Timeline';
import { useChangeStage, useCustomer360 } from './useLeads';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

export default function LeadDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isPending, isError } = useCustomer360(id);
  const changeStage = useChangeStage();

  const [logging, setLogging] = useState(false);
  const [messaging, setMessaging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [askingLost, setAskingLost] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isPending) return <Spinner />;
  if (isError || !data?.lead) return <Alert tone="error">{t('common.error')}</Alert>;

  const { lead, owner, timeline, counts } = data;

  const onStage = (next: string) => {
    if (!next) return;
    setError(null);

    // #8: `lost` needs a reason, so ask for one instead of letting the database
    // reject the request and showing the user an error they could have avoided.
    if (next === 'lost') {
      setAskingLost(true);
      return;
    }

    changeStage.mutateAsync({ id: lead.id, stage: next as LeadStage }).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    });
  };

  const confirmLost = () => {
    changeStage
      .mutateAsync({ id: lead.id, stage: 'lost', lostReason })
      .then(() => {
        setAskingLost(false);
        setLostReason('');
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : t('common.error'));
      });
  };

  // One-tap dial and WhatsApp: the users are in the field on phones, and the number
  // is already E.164 so both links are valid without further munging.
  const tel = `tel:${lead.phone_primary}`;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/leads" className="text-xs text-brand-700 hover:underline">
            ← {t('nav.leads')}
          </Link>
          <h1 className="mt-1 text-xl font-bold text-slate-900">{lead.full_name}</h1>
          <p className="text-sm text-slate-500" dir="ltr">
            {lead.phone_primary}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={tel}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            <Phone className="size-4" aria-hidden="true" />
            {t('leads.call')}
          </a>
          {/* Opens the template dialog rather than WhatsApp directly: the message
              gets pre-filled and, more importantly, the rep is asked afterwards
              whether it was actually sent. wa.me tells us nothing. */}
          <button
            type="button"
            onClick={() => setMessaging(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <MessageCircle className="size-4" aria-hidden="true" />
            {t('leads.whatsapp')}
          </button>
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {t('leads.edit')}
          </Button>
          <Button onClick={() => setLogging(true)}>{t('leads.logActivity')}</Button>
        </div>
      </div>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {lead.duplicate_hits > 0 ? (
        <Alert tone="info">{t('leads.duplicateCount', { count: lead.duplicate_hits })}</Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-1">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2">
              <StageBadge stage={lead.stage} />
              <TemperatureBadge temperature={lead.temperature} />
              <SlaBadge status={lead.sla_status} />
              <SlaCountdown dueAt={lead.sla_due_at} status={lead.sla_status} />
            </div>

            <div className="mt-4">
              <Select
                label={t('leads.changeStage')}
                value=""
                placeholder={t('leads.changeStage')}
                disabled={lead.stage === 'won' || changeStage.isPending}
                onChange={(e) => onStage(e.target.value)}
                options={[...ACTIVE_STAGES, 'won' as LeadStage, 'lost' as LeadStage]
                  .filter((s) => s !== lead.stage)
                  .map((s) => ({ value: s, label: t(`stages.${s}` as 'stages.new') }))}
              />
              {lead.stage === 'won' ? (
                <p className="mt-1 text-xs text-slate-500">{t('leads.wonIsFinal')}</p>
              ) : null}
            </div>

            {askingLost ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <label htmlFor="lost-reason" className="text-sm font-medium text-amber-900">
                  {t('leads.lostReason')}
                </label>
                <input
                  id="lost-reason"
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-amber-300 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-amber-800">{t('leads.lostReasonWhy')}</p>
                <div className="mt-2 flex gap-2">
                  <Button variant="secondary" onClick={() => setAskingLost(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    disabled={!lostReason.trim()}
                    loading={changeStage.isPending}
                    onClick={confirmLost}
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <dl className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <Field label={t('leads.owner')}>{owner?.full_name ?? '—'}</Field>
            <Field label={t('leads.source')}>
              {lead.source ? t(`sources.${lead.source}` as 'sources.meta') : '—'}
            </Field>
            <Field label={t('leads.budget')}>
              <span dir="ltr">{formatBudget(lead.budget_min, lead.budget_max)}</span>
            </Field>
            <Field label={t('leads.clientType')}>
              {lead.client_type
                ? t(`clientTypes.${lead.client_type}` as 'clientTypes.end_user')
                : '—'}
            </Field>
            <Field label={t('leads.areas')}>{lead.preferred_areas.join('، ') || '—'}</Field>
            <Field label={t('leads.captured')}>{formatDateTime(lead.captured_at)}</Field>
            <Field label={t('leads.firstContact')}>
              {lead.first_contact_at ? (
                formatDateTime(lead.first_contact_at)
              ) : (
                <span className="text-amber-700">{t('leads.neverContacted')}</span>
              )}
            </Field>
            <Field label={t('leads.lastActivity')}>
              {lead.last_activity_at ? formatRelative(lead.last_activity_at) : '—'}
            </Field>
            {lead.stage === 'lost' && lead.lost_reason ? (
              <div className="col-span-2">
                <Field label={t('leads.lostReason')}>{lead.lost_reason}</Field>
              </div>
            ) : null}
          </dl>

          <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 bg-white p-4 text-center">
            {(
              [
                ['calls', counts.calls],
                ['whatsapp', counts.whatsapp],
                ['meetings', counts.meetings],
                ['visits', counts.visits],
                ['notes', counts.notes],
                ['offers', counts.offers],
              ] as const
            ).map(([key, value]) => (
              <div key={key}>
                <div className="text-lg font-semibold text-slate-900">{value}</div>
                <div className="text-xs text-slate-500">{t(`leads.counts.${key}`)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold text-slate-800">{t('leads.timeline')}</h2>
          <Timeline entries={timeline} />
        </div>
      </div>

      {/* Deals before matches: once a unit is booked, what happened is more
          interesting than what might have. */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">{t('deals.title')}</h2>
        <DealsPanel leadId={lead.id} leadRevision={lead.updated_at} />
      </div>

      {/* Matching units (#22). Below the fold rather than in the sidebar: the score
          breakdown is the point, and it needs the width to stay readable. */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">{t('leads.matchingUnits')}</h2>
        <MatchingUnitsPanel leadId={lead.id} revision={lead.updated_at} />
      </div>

      {/* Advice, not data: this panel has no write path to the lead (#38). */}
      <AiPanel feature="recommend" leadId={lead.id} title={t('ai.recommendTitle')} />

      <WhatsAppDialog
        open={messaging}
        onClose={() => setMessaging(false)}
        leadId={lead.id}
        leadName={lead.full_name}
        phone={lead.whatsapp_phone ?? lead.phone_primary}
        stage={lead.stage}
      />

      <LogActivityDialog open={logging} leadId={lead.id} onClose={() => setLogging(false)} />

      {editing ? (
        <EditLeadDialog open={editing} lead={lead} onClose={() => setEditing(false)} />
      ) : null}
    </section>
  );
}
